import React from 'react';
import PropTypes from 'prop-types';
import { keyDownA11y } from '../shared';
import styles from './PaymentMethodGroupExpander.module.css';
import {
  getCheckoutPartProps,
  getCheckoutAlertIconSrc,
  isReviewOrderBrandRefresh,
} from '../../../pages/ReviewOrder/reviewOrderBrandRefreshClasses';

const expandIcon = require('./imgs/expand-icon.svg');
const expandLightIcon = require('./imgs/expand-light-icon.svg');
const brandRefreshCaretDown = require('./imgs/brand-refresh-caret-down.svg');
const brandRefreshCaretUp = require('./imgs/brand-refresh-caret-up.svg');
const alertIcon = require('./imgs/alert-icon.svg');

const PaymentMethodGroupExpander = props => {
  const {
    children,
    header,
    sectionTitle,
    subHeader,
    subHeaderAsset,
    isAlert,
    navigateTo,
    subHeaderClass,
    isExpanded,
    setIsExpanded,
    isSingleItem,
    showSelectedSummary,
    showPaymentHeaderWhenExpanded,
  } = props;

  if (isSingleItem) {
    return <>{children}</>;
  }

  const brandRefresh = isReviewOrderBrandRefresh();
  const alertIconSrc = getCheckoutAlertIconSrc(alertIcon);
  const brandSectionTitle = sectionTitle || header;
  const showBrandSelectedRow =
    !isExpanded || showSelectedSummary || showPaymentHeaderWhenExpanded;

  if (brandRefresh) {
    return (
      <div
        className={styles.checkoutContainerWrapper}
        data-part={getCheckoutPartProps('payment-method-expander')}
      >
        <div
          className={[
            styles.checkoutContainer,
            isExpanded ? styles.containerExpanded : null,
          ].join(' ')}
          data-part={getCheckoutPartProps('payment-method-expander-inner')}
          data-expanded={isExpanded ? 'true' : 'false'}
        >
          <p data-part={getCheckoutPartProps('payment-subtitle')}>
            {brandSectionTitle}
          </p>

          {showBrandSelectedRow ? (
            <div
              onClick={() => setIsExpanded(!isExpanded)}
              className={styles.brandRefreshCollapsedRow}
              onKeyDown={keyDownA11y(() => setIsExpanded(!isExpanded))}
              role="button"
              tabIndex={0}
              data-part={getCheckoutPartProps('payment-method-header')}
              aria-expanded={isExpanded}
            >
              {subHeaderAsset ? (
                <span
                  className={styles.brandRefreshCollapsedIcon}
                  data-part={getCheckoutPartProps('payment-method-selected-icon')}
                >
                  <img id="subHeaderAsset" alt="" src={subHeaderAsset} />
                </span>
              ) : null}
              <div
                className={styles.brandRefreshCollapsedCopy}
                data-part={getCheckoutPartProps('payment-method-selected-copy')}
              >
                <p data-part={getCheckoutPartProps('payment-method-selected-title')}>
                  {header}
                </p>
                {subHeader ? (
                  <span
                    className={`${subHeaderClass} ${isAlert ? styles.alert : null}`}
                    data-part={getCheckoutPartProps('payment-method-subheader')}
                  >
                    {subHeader}
                  </span>
                ) : null}
              </div>
              <div
                className={styles.brandRefreshCollapsedEnd}
                data-part={getCheckoutPartProps('payment-method-header-end')}
              >
                {children && isAlert ? (
                  <img id={styles.alertIcon} alt="" src={alertIconSrc} />
                ) : null}
                {children || navigateTo ? (
                  <img
                    className={navigateTo ? styles.navigate : null}
                    id={styles.expandIcon}
                    alt=""
                    src={isExpanded ? brandRefreshCaretUp : brandRefreshCaretDown}
                    data-part={getCheckoutPartProps('payment-method-caret')}
                  />
                ) : null}
              </div>
            </div>
          ) : null}

          {showSelectedSummary && isExpanded ? (
            <div
              className={styles.brandRefreshDivider}
              data-part={getCheckoutPartProps('payment-method-divider')}
              aria-hidden="true"
            />
          ) : null}

          <div
            hidden={!isExpanded}
            className={styles.containerDropDownChildren}
            data-part={getCheckoutPartProps('payment-method-body')}
          >
            {children}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.checkoutContainerWrapper}>
      <div
        className={[styles.checkoutContainer, isExpanded ? styles.containerExpanded : null].join(
          ' '
        )}
      >
        <div
          onClick={() => setIsExpanded(!isExpanded)}
          className={[styles.checkoutContainerCollapsedContainer].join(' ')}
          onKeyDown={keyDownA11y(() => {
            setIsExpanded(!isExpanded);
          })}
          role="button"
          tabIndex={0}
        >
          <div className={styles.checkoutContainerLeft}>
            <p className="font-montserratBold leading-5 text-dark dark:text-white">
              {' '}
              {subHeaderAsset ? (
                <span>
                  <img id="subHeaderAsset" alt="" src={subHeaderAsset} />
                </span>
              ) : null}
              {header}
            </p>

            <span className={`${subHeaderClass} ${isAlert ? styles.alert : null} font-montserratMedium text-xs leading-[14px]`}>
              {subHeader}
            </span>
          </div>
          <div className={styles.checkoutContainerRight}>
            {children && isAlert ? (
              <img id={styles.alertIcon} alt="" src={alertIconSrc} />
            ) : null}
            {children || navigateTo ? (
              <>
                <img
                  className={`${navigateTo ? styles.navigate : null} block dark:hidden`}
                  id={styles.expandIcon}
                  alt=""
                  src={expandIcon}
                />

                <img
                  className={`${navigateTo ? styles.navigate : null} hidden dark:block`}
                  id={styles.expandLightIcon}
                  alt=""
                  src={expandLightIcon}
                />
              </>
            ) : null}
          </div>
        </div>
        <div hidden={!isExpanded} className={styles.containerDropDownChildren}>
          {children}
        </div>
      </div>
    </div>
  );
};

PaymentMethodGroupExpander.propTypes = {
  children: PropTypes.element,
  header: PropTypes.string,
  sectionTitle: PropTypes.string,
  subHeader: PropTypes.string,
  subHeaderAsset: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  isAlert: PropTypes.bool,
  navigateTo: PropTypes.string,
  subHeaderClass: PropTypes.string,
  isExpanded: PropTypes.bool,
  setIsExpanded: PropTypes.func,
  isSingleItem: PropTypes.bool,
  showSelectedSummary: PropTypes.bool,
  showPaymentHeaderWhenExpanded: PropTypes.bool,
};

PaymentMethodGroupExpander.defaultProps = {
  children: null,
  header: '',
  sectionTitle: '',
  subHeader: '',
  subHeaderAsset: '',
  isAlert: false,
  navigateTo: '',
  subHeaderClass: '',
  isExpanded: true,
  setIsExpanded: () => {},
  isSingleItem: false,
  showSelectedSummary: false,
  showPaymentHeaderWhenExpanded: false,
};

export default PaymentMethodGroupExpander;
